import React, { useEffect, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { useHistory } from 'react-router-dom'
import { t } from 'i18next'
import { useFormik } from 'formik'
import * as yup from 'yup'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import { useDispatch, useSelector } from 'react-redux'
import MiniLoading from 'Components/MiniLoading'
import CustomSnackbar from 'Components/CustomSnackbar'
import NoImage from 'Assets/Images/Logos/no-image.png'
import { ROUTES } from 'Utils/constants'
import {
  getQuickListSettingAction,
  saveQuickListSettingAction,
} from 'redux/actions/quickListSettingsActions'
import {
  buildQuickListPayload,
  mapQuickListItemToFormValues,
  isValidProductQuantity,
  QUICK_LIST_MAX_PRODUCT_QUANTITY,
} from './utils/quickListInformation.utils'
import ProductPickerModal from './components/ProductPickerModal'
import WarehousePickerModal from './components/WarehousePickerModal'
import './QuickListInformation.css'

function QuickListInformation({ id, editMode }) {
  const dispatch = useDispatch()
  const history = useHistory()

  const userId = useSelector(
    state => state.login?.user?.CustomerInfo?.CustomerId
  )

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showProductPicker, setShowProductPicker] = useState(false)
  const [showWarehousePicker, setShowWarehousePicker] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState({
    open: false,
    message: '',
    severity: 'warning',
  })

  const validationSchema = yup.object({
    name: yup.string().required(t('QUICK_LIST_SETTINGS.FORM.REQUIRED')),
    description: yup.string(),
    warehouses: yup
      .array()
      .min(1, t('QUICK_LIST_SETTINGS.FORM.REQUIRED'))
      .required(t('QUICK_LIST_SETTINGS.FORM.REQUIRED')),
    products: yup
      .array()
      .min(1, t('QUICK_LIST_SETTINGS.FORM.REQUIRED'))
      .required(t('QUICK_LIST_SETTINGS.FORM.REQUIRED'))
      .test(
        'valid-quantities',
        t('QUICK_LIST_SETTINGS.FORM.INVALID_QUANTITY', {
          max: QUICK_LIST_MAX_PRODUCT_QUANTITY,
        }),
        products =>
          (products || []).every(product =>
            isValidProductQuantity(product.Quantity)
          )
      ),
    status: yup.boolean(),
  })

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      warehouses: [],
      products: [],
      status: true,
    },
    validationSchema,
    onSubmit: async values => {
      setIsSubmitting(true)

      try {
        const payload = buildQuickListPayload({
          values,
          editMode,
          id,
          userId,
        })

        const success = await dispatch(saveQuickListSettingAction(payload))

        if (success) {
          setSnackbarMessage({
            open: true,
            message: editMode
              ? t('QUICK_LIST_SETTINGS.MESSAGE.SUCCESS_EDIT')
              : t('QUICK_LIST_SETTINGS.MESSAGE.SUCCESS_SAVE'),
            severity: 'success',
          })
          history.push(ROUTES.quickListSettings)
        } else {
          setSnackbarMessage({
            open: true,
            message: editMode
              ? t('QUICK_LIST_SETTINGS.MESSAGE.FAIL_EDIT')
              : t('QUICK_LIST_SETTINGS.MESSAGE.FAIL_SAVE'),
            severity: 'error',
          })
        }
      } catch (error) {
        setSnackbarMessage({
          open: true,
          message: editMode
            ? t('QUICK_LIST_SETTINGS.MESSAGE.FAIL_EDIT')
            : t('QUICK_LIST_SETTINGS.MESSAGE.FAIL_SAVE'),
          severity: 'error',
        })
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (!editMode) return

    const loadItem = async () => {
      setIsLoading(true)

      try {
        const item = await dispatch(getQuickListSettingAction(id))

        if (!item) {
          history.push(ROUTES.quickListSettings)
          return
        }

        await formik.setValues(mapQuickListItemToFormValues(item))
      } catch (error) {
        setSnackbarMessage({
          open: true,
          message: t('QUICK_LIST_SETTINGS.MESSAGE.FAIL_LOAD'),
          severity: 'error',
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadItem()
  }, [editMode, id])

  const handleClose = () =>
    setSnackbarMessage({ open: false, message: '', severity: 'warning' })

  const handleQuantityChange = (index, quantity) => {
    if (quantity !== '' && !/^\d+$/.test(quantity)) return
    if (quantity !== '' && Number(quantity) > QUICK_LIST_MAX_PRODUCT_QUANTITY)
      return

    const nextProducts = [...formik.values.products]
    nextProducts[index] = { ...nextProducts[index], Quantity: quantity }
    formik.setFieldValue('products', nextProducts)
  }

  const handleRemoveProduct = index => {
    formik.setFieldValue(
      'products',
      formik.values.products.filter((_, i) => i !== index)
    )
  }

  const handleRemoveWarehouse = index => {
    formik.setFieldValue(
      'warehouses',
      formik.values.warehouses.filter((_, i) => i !== index)
    )
  }

  if (isLoading) {
    return <MiniLoading full />
  }

  return (
    <>
      <div className="quick-list-information">
        <div className="quick-list-information-card">
          <Row>
            <Col md={12} lg={6}>
              <div className="form-group">
                <label htmlFor="name">
                  {t('QUICK_LIST_SETTINGS.FORM.NAME')}
                  <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  data-testid="input-name"
                  className={
                    formik.touched.name && formik.errors.name
                      ? 'form-control error'
                      : 'form-control'
                  }
                  placeholder={t('QUICK_LIST_SETTINGS.FORM.PLACEHOLDER_NAME')}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.name && formik.errors.name && (
                  <div className="input-feedback">
                    <strong data-testid="error-name">
                      {formik.errors.name}
                    </strong>
                  </div>
                )}
              </div>
            </Col>
            <Col md={12} lg={6}>
              <div className="form-group">
                <label htmlFor="description">
                  {t('QUICK_LIST_SETTINGS.FORM.DESCRIPTION')}
                </label>
                <input
                  type="text"
                  id="description"
                  name="description"
                  data-testid="input-description"
                  className="form-control"
                  placeholder={t(
                    'QUICK_LIST_SETTINGS.FORM.PLACEHOLDER_DESCRIPTION'
                  )}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </Col>
          </Row>

          {editMode && (
            <Row>
              <Col md={12} lg={6}>
                <div className="form-group">
                  <label htmlFor="switch-status">
                    {t('QUICK_LIST_SETTINGS.FORM.STATUS')}
                  </label>
                  <div className="toggle">
                    <label className="switch">
                      <input
                        id="switch-status"
                        type="checkbox"
                        checked={!!formik.values.status}
                        onChange={() =>
                          formik.setFieldValue('status', !formik.values.status)
                        }
                        data-testid="switch-status"
                      />
                      <span className="slider round"></span>
                    </label>
                    <p className="switch-label">
                      {formik.values.status
                        ? t('QUICK_LIST_SETTINGS.FORM.ACTIVE')
                        : t('QUICK_LIST_SETTINGS.FORM.INACTIVE')}
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          <Row>
            <Col md={12} lg={6}>
              <div
                className={`quick-list-section${
                  formik.touched.products && formik.errors.products
                    ? ' alu-required-section'
                    : ''
                }`}>
                <div className="quick-list-section-header">
                  <label className="quick-list-section-title">
                    {t('QUICK_LIST_SETTINGS.FORM.PRODUCTS')}
                    <span className="required">*</span>
                  </label>
                  <button
                    type="button"
                    className="btn-add"
                    onClick={() => setShowProductPicker(true)}
                    data-testid="btn-add-products">
                    {t('QUICK_LIST_SETTINGS.FORM.ADD_PRODUCTS')}
                  </button>
                </div>

                {formik.values.products.length === 0 ? (
                  <p className="quick-list-section-empty">
                    {t('QUICK_LIST_SETTINGS.FORM.NO_PRODUCTS_SELECTED')}
                  </p>
                ) : (
                  <div className="quick-list-section-table-wrapper">
                    <table className="quick-list-section-table">
                      <thead>
                        <tr>
                          <th></th>
                          <th>{t('QUICK_LIST_SETTINGS.LIST.NAME')}</th>
                          <th>{t('QUICK_LIST_SETTINGS.PICKER.SKU')}</th>
                          <th>{t('QUICK_LIST_SETTINGS.FORM.QUANTITY')}</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formik.values.products.map((product, index) => (
                          <tr key={product.ProductId || index}>
                            <td>
                              <img
                                src={product.Images?.[0]?.URL || NoImage}
                                alt={product.ProductName || ''}
                                width={40}
                                height={40}
                                className="quick-list-section-thumb"
                              />
                            </td>
                            <td>{product.ProductName}</td>
                            <td>{product.Sku}</td>
                            <td>
                              <input
                                type="number"
                                min={1}
                                max={QUICK_LIST_MAX_PRODUCT_QUANTITY}
                                step={1}
                                className="form-control quick-list-quantity-input"
                                value={product.Quantity}
                                data-testid={`input-quantity-${product.ProductId}`}
                                onChange={event =>
                                  handleQuantityChange(
                                    index,
                                    event.target.value
                                  )
                                }
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="quick-list-row-delete"
                                onClick={() => handleRemoveProduct(index)}
                                data-testid={`btn-remove-product-${product.ProductId}`}>
                                &times;
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {formik.touched.products && formik.errors.products && (
                  <div className="input-feedback">
                    <strong data-testid="error-products">
                      {formik.errors.products}
                    </strong>
                  </div>
                )}
              </div>
            </Col>

            <Col md={12} lg={6}>
              <div
                className={`quick-list-section${
                  formik.touched.warehouses && formik.errors.warehouses
                    ? ' alu-required-section'
                    : ''
                }`}>
                <div className="quick-list-section-header">
                  <label className="quick-list-section-title">
                    {t('QUICK_LIST_SETTINGS.FORM.WAREHOUSES')}
                    <span className="required">*</span>
                  </label>
                  <button
                    type="button"
                    className="btn-add"
                    onClick={() => setShowWarehousePicker(true)}
                    data-testid="btn-add-warehouses">
                    {t('QUICK_LIST_SETTINGS.FORM.ADD_WAREHOUSES')}
                  </button>
                </div>

                {formik.values.warehouses.length === 0 ? (
                  <p className="quick-list-section-empty">
                    {t('QUICK_LIST_SETTINGS.FORM.NO_WAREHOUSES_SELECTED')}
                  </p>
                ) : (
                  <div className="quick-list-section-table-wrapper">
                    <table className="quick-list-section-table">
                      <thead>
                        <tr>
                          <th>{t('QUICK_LIST_SETTINGS.LIST.NAME')}</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {formik.values.warehouses.map((warehouse, index) => (
                          <tr key={warehouse.WarehouseId || index}>
                            <td>{warehouse.Description}</td>
                            <td>
                              <button
                                type="button"
                                className="quick-list-row-delete"
                                onClick={() => handleRemoveWarehouse(index)}
                                data-testid={`btn-remove-warehouse-${warehouse.WarehouseId}`}>
                                &times;
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {formik.touched.warehouses && formik.errors.warehouses && (
                  <div className="input-feedback">
                    <strong data-testid="error-warehouses">
                      {formik.errors.warehouses}
                    </strong>
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <div className="quick-list-information-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => history.push(ROUTES.quickListSettings)}
              disabled={isSubmitting}>
              {t('QUICK_LIST_SETTINGS.FORM.CANCEL')}
            </button>

            <button
              type="button"
              className="btn-save"
              disabled={isSubmitting}
              onClick={formik.handleSubmit}
              data-testid="btn-save-quick-list">
              {isSubmitting ? (
                <CircularProgress size="2.5rem" />
              ) : !editMode ? (
                t('QUICK_LIST_SETTINGS.FORM.SAVE')
              ) : (
                t('QUICK_LIST_SETTINGS.FORM.UPDATE')
              )}
            </button>
          </div>
        </div>
      </div>

      <ProductPickerModal
        open={showProductPicker}
        selectedProducts={formik.values.products}
        onClose={() => setShowProductPicker(false)}
        onConfirm={nextProducts => {
          formik.setFieldValue('products', nextProducts)
          setShowProductPicker(false)
        }}
      />
      <WarehousePickerModal
        open={showWarehousePicker}
        selectedWarehouses={formik.values.warehouses}
        onClose={() => setShowWarehousePicker(false)}
        onConfirm={nextWarehouses => {
          formik.setFieldValue('warehouses', nextWarehouses)
          setShowWarehousePicker(false)
        }}
      />

      <CustomSnackbar
        open={snackbarMessage.open}
        message={snackbarMessage.message}
        severity={snackbarMessage.severity}
        onClose={handleClose}
      />
    </>
  )
}

export default QuickListInformation
